// File: components/CityHub.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ViewState } from '../types';
import { Music, Mic, Star, ShoppingBag, Building, Coffee, Shield, Flame, Bird, Droplets, ChefHat, Utensils, Cookie, Heart } from 'lucide-react';
import { CITIES, STREET_FOOD_CONFIGS } from '../constants';
import { drawDerpyAndSussieInHub } from './renderer/allies/drawDerpy';

interface CityHubProps {
    onNavigate: (view: ViewState) => void;
    cityKey?: string;
    isConquered: boolean;
    hasUnreadMessages: boolean;
    onOpenSussie: () => void;
}

interface BuildingNode {
    label: string;
    type: ViewState | null;
    facilityCode: string;
    x: number;
    y: number;
    icon: React.ReactNode;
}

const LAYOUTS: Record<string, BuildingNode[]> = {
    'SEOUL': [
        { label: 'Practice Room', type: ViewState.PRACTICE_ROOM, facilityCode: 'PR', x: 20, y: 25, icon: <Music /> },
        { label: 'Starlight Global', type: ViewState.RECORD_LABEL, facilityCode: 'RL', x: 50, y: 12, icon: <Building /> },
        { label: 'TV Variety Show', type: ViewState.VARIETY_SHOW, facilityCode: 'VS', x: 80, y: 25, icon: <Mic /> },
        { label: 'Merch Store', type: ViewState.MERCH_SHOP, facilityCode: 'MS', x: 18, y: 55, icon: <ShoppingBag /> },
        { label: 'Concert Stage', type: ViewState.STAGE, facilityCode: 'ST', x: 50, y: 48, icon: <Star /> },
        { label: 'Idol Cafe', type: ViewState.CAFE, facilityCode: 'CA', x: 82, y: 55, icon: <Coffee /> },
        { label: 'Healer Han\'s', type: ViewState.HEALER_CLINIC, facilityCode: 'HC', x: 20, y: 80, icon: <Shield /> },
        { label: 'Bathhouse', type: ViewState.BATHHOUSE, facilityCode: 'BH', x: 50, y: 82, icon: <Droplets className="text-cyan-300" /> },
        { label: 'Spicy Challenge', type: ViewState.SPICY_CHALLENGE, facilityCode: 'SC', x: 80, y: 80, icon: <Flame className="text-red-500" /> },
        { label: 'Tteokbokki Stand', type: ViewState.STREET_FOOD, facilityCode: 'SF', x: 35, y: 65, icon: <ChefHat className="text-orange-400" /> },
    ],
    'DEFAULT': [
        { label: 'Concert Stage', type: ViewState.STAGE, facilityCode: 'ST', x: 50, y: 50, icon: <Star /> },
        { label: 'Practice Room', type: ViewState.PRACTICE_ROOM, facilityCode: 'PR', x: 25, y: 20, icon: <Music /> },
        { label: 'Record Label', type: ViewState.RECORD_LABEL, facilityCode: 'RL', x: 75, y: 20, icon: <Building /> },
        { label: 'Idol Cafe', type: ViewState.CAFE, facilityCode: 'CA', x: 18, y: 50, icon: <Coffee /> },
        { label: 'Spicy Challenge', type: ViewState.SPICY_CHALLENGE, facilityCode: 'SC', x: 85, y: 50, icon: <Flame className="text-red-500" /> },
        { label: 'Merch Store', type: ViewState.MERCH_SHOP, facilityCode: 'MS', x: 25, y: 75, icon: <ShoppingBag /> },
        { label: 'Bathhouse', type: ViewState.BATHHOUSE, facilityCode: 'BH', x: 75, y: 75, icon: <Droplets className="text-cyan-300" /> },
        { label: 'Street Food Stand', type: ViewState.STREET_FOOD, facilityCode: 'SF', x: 50, y: 25, icon: <ChefHat className="text-orange-400" /> },
    ]
};

export const CityHub: React.FC<CityHubProps> = ({ onNavigate, cityKey = "SEOUL", isConquered, hasUnreadMessages, onOpenSussie }) => {
    const cityData = CITIES[cityKey];
    const displayName = cityData ? cityData.name : cityKey;
    const cityConfig = STREET_FOOD_CONFIGS[cityKey] || {
        ...STREET_FOOD_CONFIGS['DEFAULT'],
        standName: `${displayName} Street Food`
    };
    const facilities = cityData ? cityData.facilities : [];

    const getStreetFoodIcon = (iconName: string) => {
        switch (iconName) {
            case 'ChefHat': return <ChefHat className="text-orange-400" />;
            case 'Utensils': return <Utensils className="text-pink-400" />;
            case 'Cookie': return <Cookie className="text-yellow-400" />;
            case 'Star': return <Star className="text-green-400" />;
            case 'Heart': return <Heart className="text-red-400" />;
            default: return <Utensils className="text-blue-400" />;
        }
    };

    const nodes = (LAYOUTS[cityKey] || LAYOUTS['DEFAULT'])
        .filter(n => facilities.includes(n.facilityCode))
        .map(n => {
            if (n.facilityCode === 'SF') {
                return {
                    ...n,
                    label: cityConfig.standName,
                    icon: getStreetFoodIcon(cityConfig.standIcon)
                };
            }
            return n;
        });

    // Default to Concert Stage instead of index 0 (Practice Room)
    const [selectedIndex, setSelectedIndex] = useState(() => {
        const stageIdx = nodes.findIndex(n => n.facilityCode === 'ST');
        return stageIdx !== -1 ? stageIdx : 0;
    });

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (nodes.length === 0) return;
        if (e.key === 'ArrowRight' || e.key === 'd') {
            setSelectedIndex(prev => (prev + 1) % nodes.length);
        } else if (e.key === 'ArrowLeft' || e.key === 'a') {
            setSelectedIndex(prev => (prev - 1 + nodes.length) % nodes.length);
        } else if (e.key === 'Enter' || e.key === ' ') {
            const node = nodes[selectedIndex];
            if (node.type) {
                onNavigate(node.type);
            }
        } else if (e.key === 'm') {
            onNavigate(ViewState.TOUR_MAP);
        }
    }, [nodes, onNavigate, selectedIndex]);

    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => { containerRef.current?.focus(); }, []);

    useEffect(() => {
        if (selectedIndex >= nodes.length) {
            const stageIdx = nodes.findIndex(n => n.facilityCode === 'ST');
            setSelectedIndex(stageIdx !== -1 ? stageIdx : 0);
        }
    }, [nodes, selectedIndex]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();

        if (rect.width === 0 || rect.height === 0) {
            return;
        }

        if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.save();
        ctx.scale(dpr, dpr);

        if (hasUnreadMessages) {
            const derpyX = rect.width * 0.95;
            const derpyY = rect.height * 0.28;
            const derpyScale = 0.4;
            const derpyFacing = -1;

            ctx.save();
            ctx.translate(derpyX, derpyY);
            ctx.scale(derpyScale * derpyFacing, derpyScale);
            drawDerpyAndSussieInHub(ctx);
            ctx.restore();
        }

        ctx.restore();
    }, [hasUnreadMessages]);

    return (
        <div
            ref={containerRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            className="h-full w-full bg-slate-950 flex flex-col items-center justify-center p-1 md:p-6 outline-none overflow-hidden relative"
        >
            <div
                className="absolute inset-0 bg-slate-900 opacity-50"
                style={{ backgroundImage: 'radial-gradient(#1e293b 1px, transparent 1px)', backgroundSize: '20px 20px' }}
            ></div>

            <div className="relative z-10 w-full max-w-5xl h-full glass-panel rounded-lg shadow-2xl flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto custom-scrollbar relative w-full h-full flex flex-col">
                    <div className="min-h-[600px] w-full h-full relative flex flex-col">

                        <button
                            onClick={onOpenSussie}
                            className="absolute top-24 right-8 z-30 flex items-center justify-center w-12 h-12 md:w-16 md:h-16 bg-slate-800/80 rounded-full border-2 border-slate-600 hover:border-cyan-400 transition-all shadow-lg group backdrop-blur-sm"
                            title="Sussie's Messages"
                        >
                            <Bird size={28} className="text-slate-400 group-hover:text-cyan-400 transition-colors" />
                            {hasUnreadMessages && (
                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-slate-800 animate-pulse"></div>
                            )}
                        </button>

                        <div className="min-h-[5rem] border-b border-slate-800 flex flex-col md:flex-row items-center justify-between p-4 md:px-8 bg-slate-950/80 backdrop-blur-md relative gap-3 shrink-0 z-20">
                            <div className="text-center md:text-left">
                                <h2 className="text-2xl md:text-3xl font-heading text-white tracking-wider flex items-center justify-center md:justify-start gap-3">
                                    {displayName.toUpperCase()}
                                    {isConquered && <Shield className="text-green-400" size={24} />}
                                </h2>
                                <div className={`text-[10px] md:text-xs tracking-widest uppercase font-bold mt-1 ${isConquered ? 'text-green-500' : 'text-red-500'}`}>
                                    Region Status: {isConquered ? 'SEALED' : 'DEMON PRESENCE DETECTED'}
                                </div>
                            </div>
                            <button
                                onClick={() => onNavigate(ViewState.TOUR_MAP)}
                                className="px-6 py-2 md:py-3 bg-pink-600 hover:bg-pink-500 text-white text-xs md:text-sm tracking-wide transition-colors rounded-lg font-bold shadow-lg w-full md:w-auto"
                            >
                                OPEN WORLD MAP [M]
                            </button>
                        </div>

                        <div className="flex-1 relative bg-slate-900/30 w-full">
                            <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-10" />

                            <div className="absolute inset-0"
                                style={{ backgroundImage: 'linear-gradient(rgba(236,72,153,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(236,72,153,0.05) 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
                            </div>

                            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20 z-15">
                                {nodes.map((node, i) => (
                                    <line key={i} x1="50%" y1="50%" x2={`${node.x}%`} y2={`${node.y}%`} stroke="#ec4899" strokeWidth="1" strokeDasharray="4 4" />
                                ))}
                            </svg>

                            {nodes.map((node, i) => {
                                const isSelected = i === selectedIndex;

                                return (
                                    <button
                                        key={i}
                                        onClick={() => {
                                            setSelectedIndex(i);
                                            if (node.type) onNavigate(node.type);
                                        }}
                                        className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300 group
                            ${isSelected ? 'scale-110 z-30' : 'scale-100 z-20 hover:scale-105'}
                        `}
                                        style={{ left: `${node.x}%`, top: `${node.y}%` }}
                                    >
                                        {isSelected && (
                                            <div className="absolute -inset-4 rounded-full border border-pink-400/30 animate-ping"></div>
                                        )}

                                        <div className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center text-2xl md:text-3xl border-2 transition-all
                            ${isSelected
                                                ? 'bg-slate-800 border-pink-400 shadow-[0_0_30px_rgba(236,72,153,0.5)] text-pink-400'
                                                : 'bg-slate-900 border-slate-700 text-slate-500 group-hover:border-slate-500'
                                            }
                        `}>
                                            {node.icon}
                                        </div>

                                        <div className={`absolute top-full mt-2 md:mt-3 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] md:text-[10px] tracking-widest px-2 py-1 md:px-3 rounded-full uppercase font-bold transition-colors shadow-sm
                            ${isSelected ? 'bg-pink-500 text-white' : 'bg-black/50 text-slate-400 backdrop-blur-sm'}
                        `}>
                                            {node.label}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};